import { create } from "zustand";
import type { PlaylistItem, ScreenDefaultItem, ScreenInfo } from "../api/types";

// Provisioning lifecycle state machine:
//   init -> registering -> pairing -> active
//   init -> active (stored credential)
//   any state -> error
export type ProvisioningState = "init" | "registering" | "pairing" | "active" | "error";

export interface PairingInfo {
  deviceId: string;
  pairingCode: string;
  pairingExpiresAt: string;
}

// What the renderer is showing right now — reported in the heartbeat so the
// operator's now-playing view is ground truth, not a clock prediction.
// channel "idle" = nothing to render this slot (ids null).
export interface CurrentPlayInfo {
  slotIndex: number | null;
  channel: "contracted" | "bonus" | "default" | "idle";
  bookingId: string | null;
  mediaId: string | null;
}

interface PlayerState {
  provisioning: ProvisioningState;
  error: string | null;
  // A condition the device is recovering from on its own, shown over whatever is
  // on screen (e.g. waiting for the operator to unpair it). null = nothing to say.
  notice: string | null;

  pairing: PairingInfo | null;
  screen: ScreenInfo | null;

  // The one content tier: operator-direct bookings hold slots like paid ones,
  // and free/throttled slots are filled by the bonus rotation over this same
  // list — there is no separate fallback pool.
  playlist: PlaylistItem[];
  // The screen's default creative — lowest tier, shown only when there is no
  // active booking at all. Never proof-of-play'd.
  screenDefault: ScreenDefaultItem | null;
  etag: string | null;
  // Date.now() + serverOffsetMs == authoritative server time.
  serverOffsetMs: number;

  online: boolean;
  lastSyncAt: number | null;
  currentSlotIndex: number | null;
  currentPlay: CurrentPlayInfo | null;

  setProvisioning: (s: ProvisioningState) => void;
  setError: (msg: string) => void;
  setNotice: (notice: string | null) => void;
  setPairing: (p: PairingInfo) => void;
  setSchedule: (args: {
    screen: ScreenInfo;
    playlist: PlaylistItem[];
    screenDefault: ScreenDefaultItem | null;
    etag: string;
    serverOffsetMs: number;
  }) => void;
  setOnline: (online: boolean) => void;
  setCurrentPlay: (play: CurrentPlayInfo) => void;
  // Heartbeat-driven clock refresh — the only offset source when syncs 304.
  setServerOffset: (serverOffsetMs: number) => void;
  markSynced: () => void;
  reset: () => void;
}

export const usePlayerStore = create<PlayerState>((set) => ({
  provisioning: "init",
  error: null,
  notice: null,

  pairing: null,
  screen: null,

  playlist: [],
  screenDefault: null,
  etag: null,
  serverOffsetMs: 0,

  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  lastSyncAt: null,
  currentSlotIndex: null,
  currentPlay: null,

  setProvisioning: (provisioning) => set({ provisioning, error: null }),
  setError: (error) => set({ provisioning: "error", error }),
  setNotice: (notice) => set({ notice }),
  setPairing: (pairing) => set({ pairing, provisioning: "pairing" }),
  setSchedule: ({ screen, playlist, screenDefault, etag, serverOffsetMs }) =>
    set({ screen, playlist, screenDefault, etag, serverOffsetMs }),
  setOnline: (online) => set({ online }),
  setCurrentPlay: (currentPlay) =>
    set({ currentPlay, currentSlotIndex: currentPlay.slotIndex }),
  setServerOffset: (serverOffsetMs) => set({ serverOffsetMs }),
  markSynced: () => set({ lastSyncAt: Date.now() }),
  reset: () =>
    set({
      pairing: null,
      screen: null,
      playlist: [],
      screenDefault: null,
      etag: null,
      serverOffsetMs: 0,
      currentSlotIndex: null,
      currentPlay: null,
      lastSyncAt: null,
    }),
}));
